import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { Link } from "react-router-dom";

/**
 * Client-side sanitiser: strip any `<a name="…"></a>` (and the equivalent
 * empty `<a id="…"></a>`) that may still exist in stored article bodies.
 * These come from legacy AutoSEO HTML that html2text left inline with
 * headings. Backend now strips them on import + read, but this render-time
 * pass is the final belt: proves the DOM will never expose a raw anchor
 * tag regardless of upstream state.
 *
 * Table-of-Contents links like `[Title](#slug)` continue to resolve —
 * `rehype-slug` assigns the same GitHub-style ID to each heading below.
 */
const LEGACY_ANCHOR_RE =
  /<a\b(?=[^>]*\b(?:name|id)\s*=\s*['"][^'"]*['"])(?![^>]*\bhref\s*=)[^>]*>\s*(?:<\/a\s*>)?/gi;
const ORPHAN_CLOSING_A_RE = /<\/a\s*>/gi;

function sanitizeSource(text) {
  if (typeof text !== "string" || !text) return "";
  let out = text;
  // Two passes catch nested / adjacent forms.
  for (let i = 0; i < 2; i += 1) {
    const before = out;
    out = out.replace(LEGACY_ANCHOR_RE, "");
    if (out === before) break;
  }
  // Sweep orphan closing `</a>` on lines that no longer contain a matching `<a`.
  out = out
    .split("\n")
    .map((line) =>
      /<\/a/i.test(line) && !/<a\b/i.test(line)
        ? line.replace(ORPHAN_CLOSING_A_RE, "")
        : line,
    )
    .join("\n");
  return out;
}

/**
 * Styled markdown renderer tuned to the StoicOs aesthetic:
 *   • Cormorant Garamond serif for headings, italic gold for emphasis
 *   • Reading font for paragraphs with classical leading
 *   • Blockquotes get a gold left rule + italic serif (used for Stoic citations)
 *   • Internal links (starting with /) use react-router; external open in new tab
 *
 * Drop-in: `<MarkdownRenderer source={mdString} />`
 */
function isInternal(href) {
  return typeof href === "string" && href.startsWith("/");
}

function isHash(href) {
  return typeof href === "string" && href.startsWith("#");
}

const components = {
  h1: ({ node, ...props }) => (
    <h1
      className="font-serif text-bone text-4xl md:text-5xl tracking-tight leading-[1.1] mt-12 mb-6"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h2
      className="font-serif text-bone text-2xl md:text-3xl tracking-tight leading-[1.15] mt-14 mb-5"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h3
      className="font-serif text-bone text-xl md:text-2xl tracking-tight leading-snug mt-10 mb-4"
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p
      className="font-reading text-bone/85 text-lg md:text-[1.18rem] leading-[1.9] my-6"
      {...props}
    />
  ),
  a: ({ node, href, children, ...props }) => {
    if (isHash(href)) {
      // In-page anchor (TOC link → heading with id assigned by rehype-slug).
      return (
        <a
          href={href}
          className="text-gold underline underline-offset-4 decoration-gold/40 hover:decoration-gold transition-colors"
          {...props}
        >
          {children}
        </a>
      );
    }
    if (isInternal(href)) {
      return (
        <Link
          to={href}
          className="text-gold underline underline-offset-4 decoration-gold/40 hover:decoration-gold transition-colors"
          {...props}
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gold underline underline-offset-4 decoration-gold/40 hover:decoration-gold transition-colors"
        {...props}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-10 md:my-12 pl-6 md:pl-8 border-l-2 border-gold/70 font-serif italic text-gold-soft text-xl md:text-2xl leading-[1.6]"
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <ul
      className="list-disc list-outside pl-6 my-6 space-y-2 font-reading text-bone/85 text-lg leading-[1.8] marker:text-gold/70"
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol
      className="list-decimal list-outside pl-6 my-6 space-y-2 font-reading text-bone/85 text-lg leading-[1.8] marker:text-gold/70"
      {...props}
    />
  ),
  li: ({ node, ...props }) => <li className="pl-1" {...props} />,
  hr: () => (
    <div className="flex items-center justify-center my-14" aria-hidden>
      <span className="h-px w-12 bg-gold/50" />
      <span className="mx-3 text-gold/60 text-xs tracking-[0.4em]">§</span>
      <span className="h-px w-12 bg-gold/50" />
    </div>
  ),
  em: ({ node, ...props }) => <em className="italic text-bone/90" {...props} />,
  strong: ({ node, ...props }) => (
    <strong className="text-bone font-medium" {...props} />
  ),
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code
        className="px-1.5 py-0.5 rounded bg-ink-elevated text-gold-soft text-[0.95em] border border-white/5"
        {...props}
      />
    ) : (
      <code
        className="block p-4 rounded bg-ink-elevated text-bone/85 text-sm leading-relaxed overflow-x-auto"
        {...props}
      />
    ),
};

export default function MarkdownRenderer({ source }) {
  const clean = sanitizeSource(source);
  return (
    <div data-testid="markdown-renderer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={components}
      >
        {clean}
      </ReactMarkdown>
    </div>
  );
}
